import type * as React from "react";
import { z } from "zod";

export const localStateStorageSchema = z.object({
	stepId: z.number(),
	configId: z.number(),
	currentFieldIndex: z.number().default(0),
	// z.undefined() is required because whenever we update a field, the whole form state is saved
	// including the field keys not yet specified by the user
	values: z.record(
		z.string(),
		z.union([z.string(), z.number(), z.undefined()]),
	),
	keystrokes: z.record(
		z.string(),
		z.object({ first: z.number(), last: z.number() }),
	),
});
export type LocalStateStorageSchema = z.infer<typeof localStateStorageSchema>;

export const fileValueSchema = z
	.object({
		filename: z.string(),
		base64Data: z.string(),
		size: z.number(),
	})
	.nullable();

export type FileValue = z.infer<typeof fileValueSchema>;

export const submitPayloadSchema = z.object({
	responses: z.record(
		z.string(),
		z.union([z.string(), z.number(), fileValueSchema]),
	),
	configId: z.number(),
	keystrokes: z.record(
		z.string(),
		z.object({ first: z.number(), last: z.number() }),
	),
});
export type SubmitPayloadSchema = z.infer<typeof submitPayloadSchema>;

export const choiceSchema = z.object({
	id: z.number(),
	index: z.number(),
	name: z.string(),
});

export type Choice = z.infer<typeof choiceSchema>;

export const fieldTypeSchema = z.enum(["text", "select", "long_text", "file"]);

const fieldSchema = z.object({
	id: z.number(),
	index: z.number(),
	type: fieldTypeSchema,
	identifier: z.number(),
	description: z.string(),
	label: z.string(),
	choices: z.array(choiceSchema).nullable(),
	required: z.boolean(),
});
export type Field = z.infer<typeof fieldSchema>;

const triggerChoiceSchema = z
	.object({
		stepId: z.number(),
		fieldId: z.number(),
		choiceId: z.number(),
	})
	.nullable();
export type TriggerChoice = z.infer<typeof triggerChoiceSchema>;

const stepSchema = z.object({
	id: z.number(),
	name: z.string(),
	fields: z.array(fieldSchema),
	triggerChoice: triggerChoiceSchema,
	parentStepId: z.number().nullable(),
});
export type StepI = z.infer<typeof stepSchema>;

export const wizardFormSchema = z.object({
	steps: z.array(stepSchema),
	configId: z.number(),
});
export type WizardFormConfig = z.infer<typeof wizardFormSchema>;

export interface WizardFormRef {
	submitForm: () => Promise<void>;
}

export type OnKeystrokePayload = {
	key: string;
	fieldKey: string | null;
	timestamp: number;
};

export type OnNextPayload = {
	fromStepId: number;
	fromFieldId: number;
};

export type OnFileUploadPayload = {
	fieldKey: string;
	timestamp: number;
};

export type OnSelectChoicePayload = {
	fieldKey: string;
	timestamp: number;
};

export type WizardFormProps = {
	config: WizardFormConfig;
	previewMode: boolean;
	sendData: (payload: SubmitPayloadSchema) => Promise<void>;
	formRef?: React.RefObject<WizardFormRef> | undefined;
	onKeystroke?: ((payload: OnKeystrokePayload) => void) | undefined;
	onNext?: ((payload: OnNextPayload) => void) | undefined;
	onFileUpload?: ((payload: OnFileUploadPayload) => void) | undefined;
	onSelectChoice?: ((payload: OnSelectChoicePayload) => void) | undefined;
};

export type FormValues = Record<string, string | number | FileValue>;
